import React, { useState } from 'react';
import { auth, db } from '../../firebase';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    sendEmailVerification,
    signOut,
    updateProfile,
    sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import './SignIn.css';

// --- ICONIȚE SVG MODERNE (Vectoriale) ---
const EyeIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20" height="20" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round"
    >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
        <circle cx="12" cy="12" r="3"></circle>
    </svg>
);

const EyeOffIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20" height="20" viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round"
    >
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
        <line x1="1" y1="1" x2="23" y2="23"></line>
    </svg>
);

function SignIn() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [isSigningUp, setIsSigningUp] = useState(false);

    // Stare pentru vizibilitatea parolei
    const [showPassword, setShowPassword] = useState(false);
    // Stare pentru mesaje de eroare (banner roșu)
    const [errorMessage, setErrorMessage] = useState('');

    const navigate = useNavigate();

    const handleAuth = async (e) => {
        e.preventDefault();
        setErrorMessage(''); // Resetăm erorile la o nouă încercare

        try {
            if (isSigningUp) {
                // --- LOGICA DE ÎNREGISTRARE (SIGN UP) ---
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                const user = userCredential.user;

                await updateProfile(user, { displayName: fullName });

                // Salvăm datele în Firestore
                await setDoc(doc(db, "users", user.uid), {
                    uid: user.uid,
                    fullName: fullName,
                    email: email,
                    createdAt: new Date(),
                    subscriptionStatus: "inactive",
                    plan: "free",
                    role: "client"
                });

                await sendEmailVerification(user);
                await signOut(auth);

                // Mesaj Succes + Spam Warning
                alert(
                    `Cont creat cu succes! \n\nAm trimis un link de verificare la ${email}.\n\nIMPORTANT: Te rugăm să verifici și folderul SPAM sau JUNK dacă nu primești emailul în câteva minute.`
                );

                setFullName('');
                setEmail('');
                setPassword('');
                setIsSigningUp(false);
            } else {
                // --- LOGICA DE AUTENTIFICARE (LOGIN) ---
                const userCredential = await signInWithEmailAndPassword(auth, email, password);
                const user = userCredential.user;

                if (user.emailVerified) {
                    navigate('/'); // Redirecționare
                } else {
                    await signOut(auth);
                    setErrorMessage("Adresa de email nu este verificată. Te rugăm să verifici inbox-ul și spam-ul.");
                }
            }
        } catch (error) {
            console.error(error);

            // --- TRADUCERE ERORI ---
            let msg = "A apărut o eroare. Încearcă din nou.";
            if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
                msg = "Email sau parolă incorectă.";
            } else if (error.code === 'auth/email-already-in-use') {
                msg = "Există deja un cont cu acest email.";
            } else if (error.code === 'auth/invalid-email') {
                msg = "Adresa de email nu este validă.";
            } else if (error.code === 'auth/too-many-requests') {
                msg = "Prea multe încercări. Așteaptă câteva minute.";
            } else if (error.code === 'auth/weak-password') {
                msg = "Parola este prea slabă (minim 6 caractere).";
            }
            setErrorMessage(msg);
        }
    };

    const handlePasswordReset = async () => {
        if (!email) {
            setErrorMessage("Introdu adresa de email mai sus pentru a reseta parola.");
            return;
        }

        try {
            await sendPasswordResetEmail(auth, email);
            alert(`Email de resetare trimis la ${email}. Verifică și în SPAM!`);
            setErrorMessage('');
        } catch (error) {
            console.error("Error sending reset email:", error);
            setErrorMessage("Eroare la trimitere. Verifică adresa de email.");
        }
    };

    return (
        <div className="signin-container">
            <div className="signin-card">
                <h2 className="signin-title">
                    {isSigningUp ? "Creează " : "Bine ai "}
                    <span>{isSigningUp ? "Cont" : "Revenit"}</span>
                </h2>

                {/* Banner Eroare */}
                {errorMessage && (
                    <div className="error-banner">
                        {errorMessage}
                    </div>
                )}

                <form onSubmit={handleAuth} className="signin-form">

                    {isSigningUp && (
                        <input
                            className="signin-input"
                            type="text"
                            placeholder="Nume Complet"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            required
                        />
                    )}

                    <input
                        className="signin-input"
                        type="email"
                        placeholder="Adresa de Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />

                    {/* INPUT PAROLĂ CU OCHI */}
                    <div className="password-wrapper">
                        <input
                            className="signin-input password-input"
                            type={showPassword ? "text" : "password"}
                            placeholder="Parola"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                        <button
                            type="button"
                            className="toggle-password-btn"
                            onClick={() => setShowPassword(!showPassword)}
                            tabIndex="-1"
                            title={showPassword ? "Ascunde parola" : "Arată parola"}
                        >
                            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                        </button>
                    </div>

                    {!isSigningUp && (
                        <div style={{ textAlign: 'right', width: '100%', marginBottom: '10px' }}>
                            <button
                                type="button"
                                onClick={handlePasswordReset}
                                className="forgot-password-link"
                            >
                                Ai uitat parola?
                            </button>
                        </div>
                    )}

                    <button type="submit" className="signin-btn">
                        {isSigningUp ? "Înregistrează-te" : "Autentificare"}
                    </button>
                </form>

                <p className="signin-switch">
                    {isSigningUp ? "Ai deja cont? " : "Nou pe FitSimplu? "}
                    <button
                        onClick={() => {
                            setIsSigningUp(!isSigningUp);
                            setErrorMessage('');
                            setFullName('');
                            setPassword('');
                        }}
                        className="signin-switch-btn"
                    >
                        {isSigningUp ? "Intră în cont" : "Creează cont"}
                    </button>
                </p>
            </div>
        </div>
    );
}

export default SignIn;